import type { ReactNode } from "react";
import { AlertCircle, CloudOff, Radio, ShieldCheck } from "lucide-react";

export function PageHeader({ eyebrow, title, description, action }: { eyebrow?: string; title: string; description: string; action?: ReactNode }) {
  return <header className="page-header">
    <div><span className="eyebrow">{eyebrow ?? "FIELD NOTES / CONTROL"}</span><h1>{title}</h1><p>{description}</p></div>
    {action && <div className="page-action">{action}</div>}
  </header>;
}

export function Card({ children, className = "", title, kicker, action }: { children: ReactNode; className?: string; title?: string; kicker?: string; action?: ReactNode }) {
  return <section className={`card ${className}`}>
    {(title || action) && <div className="card-head"><div>{kicker && <span className="kicker">{kicker}</span>}{title && <h2>{title}</h2>}</div>{action}</div>}
    {children}
  </section>;
}

export function StatusPill({ source = "live", shadow = false }: { source?: string; shadow?: boolean }) {
  const offline = source === "cached" || source === "unavailable";
  return <span className={`status-pill ${offline ? "offline" : "online"}`}>
    {offline ? <CloudOff size={14} /> : <Radio size={14} />} {source === "cached" ? "Cached / PC offline" : source === "unavailable" ? "PC unavailable" : shadow ? "Live · Shadow mode" : "Live from pipeline PC"}
  </span>;
}

export function Metric({ label, value, note, accent = false }: { label: string; value: ReactNode; note?: string; accent?: boolean }) {
  return <div className={`metric ${accent ? "accent" : ""}`}><span>{label}</span><strong>{value}</strong>{note && <small>{note}</small>}</div>;
}

export function Progress({ label, value, target, verified }: { label: string; value: number; target: number; verified?: string }) {
  const pct = Math.min(100, target ? value / target * 100 : 0);
  return <div className="progress-block">
    <div className="progress-label"><span>{label}</span><strong>{value.toLocaleString()} <em>/ {target.toLocaleString()}</em></strong></div>
    <div className="progress-track"><span style={{ width: `${pct}%` }} /></div>
    <div className="progress-foot"><span>{pct.toFixed(1)}% complete</span>{verified && <span><ShieldCheck size={12} /> {verified}</span>}</div>
  </div>;
}

export function EmptyState({ title, children }: { title: string; children: ReactNode }) {
  return <div className="empty"><AlertCircle /><h3>{title}</h3><p>{children}</p></div>;
}

export function Freshness({ at }: { at?: string | null }) {
  return <span className="freshness">Data current as of {at ? new Date(at).toLocaleString() : "not yet synced"}</span>;
}

